// @ts-expect-error no types available
import * as zephrBrowser from "@zephr/browser";
import {
  PAYWALL_CLICKED_EVENT,
  ZEPHR_CUSTOM_DATA_REQUEST,
  ZEPHR_DISABLED_EVENT,
  ZEPHR_ERROR_EVENT,
  ZEPHR_REGISTER_CUSTOM_DATA_EVENT,
  ZEPHR_TIMEOUT_EVENT,
} from "./events.js";
import {
  fetchZephrJWT,
  getFetcher,
  initZephrDataLayer,
  logZephrError,
  rewriteUrl,
  storeZephrOutcome,
} from "./zephr_utils";

const collectedCustomData: Record<string, any>[] = [];

document.addEventListener(ZEPHR_REGISTER_CUSTOM_DATA_EVENT, (event: Event) => {
  const customEvent = event as CustomEvent;
  collectedCustomData.push(customEvent.detail);
});

export class Zephr extends HTMLElement {
  get envBaseUrl(): string {
    return this.getAttribute("envBaseUrl") ?? "";
  }

  get forwarderurl(): string {
    return this.getAttribute("forwarderurl") ?? "";
  }

  get isDisabled(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const queryParamDisabled =
      urlParams.get("zephr") === "false" || urlParams.get("zephr") === "0";
    return this.hasAttribute("disabled") || queryParamDisabled;
  }

  get loggedIn(): boolean {
    return this.getAttribute("loggedin") === "true";
  }

  get tenant(): string {
    return this.getAttribute("tenant") ?? "";
  }

  get debug(): boolean {
    return this.hasAttribute("debug");
  }

  get isDebugMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return (
      !this.isProd() &&
      (urlParams.get("zephr") === "false" || urlParams.get("zephr") === "0")
    );
  }

  get vendorId(): string {
    return this.getAttribute("vendorid") ?? "";
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.onErrorEvent = this.onErrorEvent.bind(this);
  }

  protected isProd(): boolean {
    return !(
      this.envBaseUrl.includes("stage") ||
      this.envBaseUrl.includes("dev") ||
      this.envBaseUrl.includes("localhost")
    );
  }

  protected onErrorEvent(event: Event) {
    const customEvent = event as CustomEvent;
    const error = customEvent.detail.error;
    this.logError(error);
  }

  async connectedCallback() {
    const consent = await this.#getConsent();

    if (consent) {
      initZephrDataLayer();

      document.addEventListener(ZEPHR_ERROR_EVENT, this.onErrorEvent);

      document.addEventListener(
        PAYWALL_CLICKED_EVENT,
        this.#handlePaywallClick,
      );

      if (!window.__zephrInitStarted && !this.isDisabled) {
        window.__zephrInitStarted = true;
        window.__zephrInit = this.initializeZephr();
      } else if (this.isDisabled) {
        document.dispatchEvent(new CustomEvent(ZEPHR_DISABLED_EVENT));
      }
    }

    const checkForTcfApiEvents = () => {
      if (typeof window.__tcfapi === "function") {
        const sourcepoint = document.querySelector("ws-sourcepoint");

        sourcepoint?.tcfApiEventlistener(
          this.#updateConsent.bind(this),
          this.#updateConsent.bind(this),
        );

        clearInterval(intervalId);
      }
    };

    const intervalId = setInterval(checkForTcfApiEvents, 200);
  }

  #handlePaywallClick() {
    storeZephrOutcome(this.tenant);
  }

  async #getConsent(): Promise<boolean> {
    const sourcepoint = document.querySelector("ws-sourcepoint");

    if (!sourcepoint) return false;

    await window.customElements.whenDefined("ws-sourcepoint");

    await sourcepoint.waitForTcfApi();

    try {
      const data = await new Promise<any>((resolve, reject) => {
        try {
          window.__tcfapi("getCustomVendorConsents", 2, (result: any) => {
            resolve(result);
          });
        } catch (err) {
          reject(err);
        }
      });

      const hasConsent = data?.consentedVendors?.some(
        (vendor: any) => vendor._id === this.vendorId,
      );

      return hasConsent && !sourcepoint.isPURSubscriber;
    } catch (err) {
      console.error("Error getting consent: ", err);
      return false;
    }
  }

  async #updateConsent() {
    const consent = await this.#getConsent();
    if (consent) {
      initZephrDataLayer();

      document.addEventListener(ZEPHR_ERROR_EVENT, this.onErrorEvent);

      document.addEventListener(
        PAYWALL_CLICKED_EVENT,
        this.#handlePaywallClick,
      );

      if (!window.__zephrInitStarted && !this.isDisabled) {
        window.__zephrInitStarted = true;
        window.__zephrInit = this.initializeZephr();
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ZEPHR_ERROR_EVENT, this.onErrorEvent);
    document.removeEventListener(
      PAYWALL_CLICKED_EVENT,
      this.#handlePaywallClick,
    );
  }

  logError(error: Error) {
    console.error("[zephr-init] Error:", error);
    const resUrl = rewriteUrl(window.location.href, this.envBaseUrl);
    logZephrError(error, this.forwarderurl, resUrl || this.envBaseUrl);
  }

  async initializeZephr(): Promise<void> {
    const controller = new AbortController();
    try {
      const jwt = await fetchZephrJWT(this.envBaseUrl, this.loggedIn);

      if (collectedCustomData.length === 0) {
        const requestEvent = new CustomEvent(ZEPHR_CUSTOM_DATA_REQUEST);
        document.dispatchEvent(requestEvent);
      }

      const mergedCustomData = Object.assign(
        { referer: document.referrer },
        ...collectedCustomData,
      );

      await Promise.race([
        zephrBrowser.run({
          cdnApi: "",
          customData: mergedCustomData,
          debug: this.isDebugMode,
          jwt,
          fetcher: getFetcher(controller),
        }),
        new Promise<void>((_, reject) =>
          setTimeout(() => {
            controller.abort();
            document.dispatchEvent(new CustomEvent(ZEPHR_TIMEOUT_EVENT));
            reject(new Error("timeout"));
          }, 2000),
        ),
      ]);
    } catch (error) {
      console.error("Zephr error:", (error as Error).message);
      this.logError(error as Error);
    }
  }
}

if (!customElements.get("ws-zephr")) {
  customElements.define("ws-zephr", Zephr);
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __zephrInit?: Promise<void>;
    __zephrInitStarted?: boolean;
  }
}
